import { type UseQueryResult } from '@tanstack/react-query'

import { type CurrencyCodes } from 'shared/constants/currency'
import { type IPlatform } from 'src/dataSource/constants'

import { type PLATFORM } from 'src/dataSource/types'

import { type Platforms } from './accounts'
import fetch, { type ClientError } from './fetch'
const client = fetch()

export type ScenarioBreakdown = 'data_source' | 'channel'
export type ScenarioAllocationConstraint = 'no_limits' | 'within_channels' | 'within_data_sources'
export type ScenarioRiskToleranceMode = 'safe' | 'normal' | 'bold'
/** Decimal fraction of current spend (0.5 = 50%). */
export type ScenarioSpendCapFraction = number
export type ScenarioTargetType = 'revenue' | 'conversions'
export type ScenarioOptimizationGoal =
  | 'budget_factor'
  | 'target_spend'
  | 'target_roas'
  | 'target_cpa'
  | 'target_conversions'
  | 'target_revenue'
  | 'revenue_factor'
  | 'conversion_factor'

export interface ScenarioApiResp {
  scenarioId: string
  scenarioName: string
  targetType: ScenarioTargetType
  optimizationGoal: ScenarioOptimizationGoal
  goalTargetValue: number
  /** Backend-computed conversion of `goalTargetValue` from the scenario's `currency` into the current client's currency. Only populated for monetary goals. Read-only — must not be sent back to the backend. */
  goalTargetValueClientCurrency?: number
  breakdowns: ScenarioBreakdown[]
  lookbackDays: 30 | 60 | 90 | 120
  riskToleranceMode: ScenarioRiskToleranceMode
  allocationConstraint: ScenarioAllocationConstraint
  currency: CurrencyCodes
  /** Defaults to 0.5 when omitted (older payloads). */
  maxSpendIncrease?: ScenarioSpendCapFraction
  /** Defaults to 0.5 when omitted (older payloads). */
  maxSpendDecrease?: ScenarioSpendCapFraction
}

/** Write-only shape sent to the backend. Omits backend-computed read-only fields. */
export type ScenarioApiReq = Omit<ScenarioApiResp, 'goalTargetValueClientCurrency'>

export interface PerformanceCurvesAttributes {
  scenarios: ScenarioApiResp[]
}

export enum CustomDataSourceType {
  ONLINE_SPEND = 'online_spend',
  TARGET = 'target',
  OFFLINE_SPEND = 'offline_spend'
}

export type CreditInput = 'xgboost' | 'ddav2' | 'native' | 'rba' | 'lnda'
export type BOModelType = 'ga4Default' | 'ga4Lnda'

/** Credit input values returned by API for Budget Optimiser native models */
export const NATIVE_BO_CREDIT_INPUT = ['native', 'lnda'] as const
export type NativeBOCreditInput = (typeof NATIVE_BO_CREDIT_INPUT)[number]

export const isNativeBOCreditInput = (value: string): value is NativeBOCreditInput => {
  return (NATIVE_BO_CREDIT_INPUT as readonly string[]).includes(value)
}

export type ModelType = CreditInput | BOModelType

export type RevenueModelingMethod = 'daily_average' | 'separate_models'

export interface AttributionResp {
  id: number
  clientId: number
  clientName: string
  name: string
  sourceProjectId: number
  sourceProject: string
  sourceAuthAccount: string
  targetProjectId: string
  targetProject: string
  targetAuthAccount: string
  sourceDataset: string
  targetDataset: string
  targetCurrency?: string
  lookbackDays: number
  xgboostLookback?: number
  revenueModelingMethod?: RevenueModelingMethod
  customDataSources?: CustomDataSourcesRes[]
  xgboostModelLookback?: number
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  /** Omitted by some API responses; only explicit `false` means budget-optimiser-only. */
  creditAllocator?: boolean
  performanceCurves: boolean
  reallocations: Reallocation[]
  analyticsAccount?: AnalyticsAccountRes
  adAccounts: AdAccountOutput[]
  runSchedule: boolean
  schedule: string
  creditInput: CreditInput
  channelGroupingId?: number
  channelGroupingMetadata?: ChannelGroupingMetadata
  includeIntercept?: boolean
  recencyWeighting?: number
  performanceCurvesAttributes?: PerformanceCurvesAttributes
}

export interface AttributionModelReq {
  name: string
  clientId: number
  creditInput: CreditInput
  lookbackDays: number
  xgboostLookback?: number
  xgboostModelLookback?: number
  revenueModelingMethod?: RevenueModelingMethod
  adAccounts: AdAccountReq[] | null
  customDataSources?: CustomDataSourcesReq[]
  customConversionSources?: CustomDataSourcesReq[]
  sourceProjectId: number
  targetProjectId: number
  sourceDataset: string
  targetDataset: string
  runSchedule: boolean
  creditAllocator: boolean
  performanceCurves: boolean
  reallocations: Reallocation[]
  analyticsAccount?: AnalyticsAccountReq
  channelGroupingId: number
  targetCurrency?: string
  channelGroupingMetadata: ChannelGroupingMetadata
  includeIntercept?: boolean
  recencyWeighting?: number
  performanceCurvesAttributes?: PerformanceCurvesAttributes
}

interface Reallocation {
  channel: string
  allocation: number
}

interface AnalyticsAccountRes {
  accountId: number
  filterConditions?: FilterConditionRes[]
  externalAccountId: string
  platform: string
  has360?: boolean
  accountName: string
  channelGrouping: [ChannelDefinitionAnalytics]
  userIdIndex?: number
  channelGroupingSql?: string
  credentialsUsed: string
  conversionField: string
}

interface AnalyticsAccountReq {
  accountId: number
  filterConditions?: FilterConditionReq[]
  platform: string
  conversionField?: string
}

interface Rule {
  field: string
  operator: string
  value: string
}

interface ChannelGrouping {
  exclude: boolean
  impressionChannel: boolean
  name: Platforms
  rules: Rule
}

interface ChannelDefinitionAnalytics {
  name: string
  exclude?: boolean
  rules: Rule
}

export interface FilterConditionRes {
  filterConditionSql?: string
  filterConditionSqlEventAttributionConnector?: string
  filterConditionSqlSessionAttributionConnector?: string
  name: string
  rules: Rule[][]
}

interface FilterConditionReq {
  name: string
  rules: Rule[][]
}

type AdAccountOutput = {
  accountId: number
  accountName: string
  channelGrouping: ChannelGrouping
  channelGroupingSql: string
  connectorsConfigs: number
  externalAccountId: string
  filterConditions: FilterConditionRes[]
  gclidBiddingChannels?: string
  impressionsChannels?: string
  platform: Platforms
  filterConditionsSql?: string
  conversionField?: string
  clickField?: string
} & Pick<IPlatform, 'iconUrl'>

interface CustomDataSourcesReq {
  platform: typeof PLATFORM.GOOGLE_BIGQUERY | typeof PLATFORM.GOOGLE_SHEETS
  accountId: number
  worksheetId?: string
  datasetId?: string
  tableId?: string
  type: CustomDataSourceType
  validationDays?: number
}

interface CustomDataSourcesRes {
  platform: typeof PLATFORM.GOOGLE_BIGQUERY | typeof PLATFORM.GOOGLE_SHEETS
  accountId: number
  worksheetId?: string
  worksheetName?: string
  datasetId?: string
  tableId?: string
  accountName?: string
  sourceAuthAccount?: string
  type: CustomDataSourceType
  validationDays?: number
}

interface AdAccountReq {
  accountId: number
  platform: string
  filterConditions?: [FilterConditionReq]
  conversionField?: string
  clickField?: string
  gclidBiddingChannels?: [string]
  impressionChannels?: [string]
}

export type LiftTestGoal = 'revenue' | 'conversions'
export interface LiftTestMetadata {
  lift: number
  /** Direct standard error. Required when confidenceInterval and relativeUncertainty are absent. */
  se?: number
  /** 95% confidence interval bounds. Mutually exclusive with se and relativeUncertainty. */
  confidenceInterval?: { lower: number; upper: number }
  /** Relative uncertainty as a percentage. Mutually exclusive with se and confidenceInterval. */
  relativeUncertainty?: number
  testStartDate: string
  testEndDate: string
  goal: LiftTestGoal
  spendCurrency: string
  descriptiveName: string
  enabled: boolean
}

export type ChannelGroupingMetadata = Record<
  string,
  {
    exclude: boolean
    impressionChannel: boolean
    reallocation: number
    meanPrior?: number
    adStock?: number
    liftTests?: LiftTestMetadata[]
  }
>

export interface ConfigIdAndClientIdReq {
  clientId: number
  modelId: number
}

export const CUSTOM_DATA_SOURCES_SUPPORTED_PLATFORMS = {
  GOOGLE_BIGQUERY: 'google_bigquery',
  GOOGLE_SHEETS: 'google_sheets'
} as const

const paths = {
  integrationAttributions: '/integrated_attributions'
}

export interface IntegrationAttributionsResp extends Array<AttributionResp> {}

export const getIntegrationAtributions = async (clientId: number): Promise<IntegrationAttributionsResp> => {
  return await client.get<IntegrationAttributionsResp>({
    path: `${paths.integrationAttributions}?client_id=${clientId}&credit_allocator=true`
  })
}

export const getIntegrationAtribution = async (modelId: number, clientId: number): Promise<AttributionResp> => {
  return await client.get<AttributionResp>({
    path: `${paths.integrationAttributions}/${modelId}?client_id=${clientId}`
  })
}

export const createIntegrationAtribution = async (data: AttributionModelReq): Promise<AttributionResp> => {
  const path = paths.integrationAttributions
  return await client.post<AttributionResp>({
    path,
    data
  })
}

export const deleteIntegrationAtribution = async (data: ConfigIdAndClientIdReq): Promise<AttributionResp> => {
  const path = `${paths.integrationAttributions}/${data.modelId}?client_id=${data.clientId}`
  return await client.del<AttributionResp>({
    path
  })
}

export const updateIntegrationAtribution = async ({
  data,
  configId
}: {
  data: AttributionModelReq
  configId: number
}): Promise<AttributionResp> => {
  const path = `${paths.integrationAttributions}/${configId}`
  return await client.put<AttributionResp>({
    path,
    data
  })
}

export type ValidationErrorCodes =
  | 'INVALID_CHANNEL'
  | 'INVALID_DATE'
  | 'INVALID_SPEND'
  | 'INVALID_CURRENCY'
  | 'REQUIRED_FIELD_MISSING'
  | 'TYPE_MISMATCH'
  | 'UNKNOWN_VALIDATION_ERROR'
  | 'GOOGLE_CLOUD_BAD_REQUEST'

interface ValidationResp {
  message: string
  dataSourceId: string
}

export type AttributionValidationQueryResult = UseQueryResult<ValidationResp, ClientError>

export const validateCustomDataSources = async (
  urlParams: URLSearchParams
): Promise<AttributionValidationQueryResult> => {
  const path = `${paths.integrationAttributions}/validate_custom_data_source?${urlParams.toString()}`
  return await client.get<AttributionValidationQueryResult>({ path })
}
